import { useState } from "react";
import { Loader2, UserPlus, X } from "lucide-react";
import {
  CHAT_MEETING_REMIND_DEFAULT,
  defaultMeetingLocalValue,
  normalizeMeetingRemindMinutes,
} from "@/lib/meetingsApi";

type Props = {
  busy?: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    email: string;
    mode: "now" | "later";
    startsAt: string;
    duration: number;
    remindMinutes: number;
  }) => Promise<void>;
};

export function ChatGuestInviteModal({ busy, onClose, onSubmit }: Props) {
  const [email, setEmail] = useState("");
  const [mode, setMode] = useState<"now" | "later">("now");
  const [startsAt, setStartsAt] = useState(defaultMeetingLocalValue);
  const [duration, setDuration] = useState(60);
  const [remindMinutes, setRemindMinutes] = useState(CHAT_MEETING_REMIND_DEFAULT);
  const [saving, setSaving] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  const locked = Boolean(busy || saving);

  const go = async () => {
    const mail = email.trim();
    if (!mail || !mail.includes("@")) {
      setHint("Indiquez un e-mail valide.");
      return;
    }
    setSaving(true);
    setHint(null);
    try {
      await onSubmit({ email: mail, mode, startsAt, duration, remindMinutes });
    } catch (e) {
      setHint(e instanceof Error ? e.message : "Impossible d’inviter.");
      setSaving(false);
      return;
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
      <button type="button" className="absolute inset-0" aria-label="Fermer" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-t-2xl border border-[var(--chat-border)] bg-[var(--chat-surface)] p-4 shadow-[var(--chat-shadow)] sm:rounded-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-semibold">
            <UserPlus className="size-4 text-[var(--chat-accent-strong)]" />
            Invité externe
          </h3>
          <button type="button" className="rounded-lg p-1 text-[var(--chat-muted)]" onClick={onClose}>
            <X className="size-4" />
          </button>
        </div>
        <p className="mb-3 text-xs text-[var(--chat-muted)]">
          Hors Suite : lien par e-mail, sans compte. Crée une salle si besoin.
        </p>
        <label className="mb-3 block space-y-1">
          <span className="text-xs text-[var(--chat-muted)]">E-mail</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="marie@client.fr"
            disabled={locked}
            className="w-full rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--chat-accent)]"
          />
        </label>
        <div className="mb-3 flex gap-2">
          <button
            type="button"
            className={`flex-1 rounded-xl px-3 py-2 text-sm font-medium ${
              mode === "now"
                ? "bg-[var(--chat-accent-soft)] text-[var(--chat-accent-strong)]"
                : "bg-[var(--chat-surface-elevated)] text-[var(--chat-muted)]"
            }`}
            onClick={() => setMode("now")}
            disabled={locked}
          >
            Maintenant
          </button>
          <button
            type="button"
            className={`flex-1 rounded-xl px-3 py-2 text-sm font-medium ${
              mode === "later"
                ? "bg-[var(--chat-accent-soft)] text-[var(--chat-accent-strong)]"
                : "bg-[var(--chat-surface-elevated)] text-[var(--chat-muted)]"
            }`}
            onClick={() => setMode("later")}
            disabled={locked}
          >
            Planifier
          </button>
        </div>
        {mode === "later" ? (
          <div className="mb-3 space-y-2">
            <input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              disabled={locked}
              className="w-full rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--chat-accent)]"
            />
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              disabled={locked}
              className="w-full rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--chat-accent)]"
            >
              <option value={30}>30 min</option>
              <option value={60}>1 h</option>
              <option value={90}>1 h 30</option>
              <option value={120}>2 h</option>
            </select>
            <select
              value={remindMinutes}
              onChange={(e) => setRemindMinutes(normalizeMeetingRemindMinutes(Number(e.target.value)))}
              disabled={locked}
              className="w-full rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--chat-accent)]"
            >
              <option value={0}>Pas de rappel</option>
              <option value={5}>Rappel 5 min avant</option>
              <option value={10}>Rappel 10 min avant</option>
              <option value={15}>Rappel 15 min avant</option>
              <option value={60}>Rappel 1 h avant</option>
            </select>
          </div>
        ) : null}
        <button
          type="button"
          disabled={locked}
          onClick={() => void go()}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-[var(--chat-accent)] py-2.5 text-sm font-semibold text-[#0f172a] disabled:opacity-40"
        >
          {saving ? <Loader2 className="size-4 animate-spin" /> : null}
          {saving ? "Invitation…" : mode === "now" ? "Lancer et inviter" : "Planifier et inviter"}
        </button>
        {hint ? <p className="mt-2 text-xs text-[var(--chat-danger)]">{hint}</p> : null}
      </div>
    </div>
  );
}
